import { Formik, FormikValues } from 'formik';
import React, { useContext } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from '../CreateOrEditCategoryModal/styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import ProjectSelect from '../../inputs/ProjectSelect';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { DailyMissionTemplate, MISSION_TARGET_TYPES, MissionTargetType } from '../../../api/types/progress';
import { MinimalDailyMissionTemplateSchema } from '../../../api/progress/parser';
import { CreateDailyMissionTemplateDto } from '../../../api/typeDtos/progress';
import { createDailyMissionTemplate, updateDailyMissionTemplate } from '../../../api/progress';
import { textFieldUtils } from '../../../utils/textFields';

const TARGET_TYPE_LABELS: Record<MissionTargetType, string> = {
  sessionsStarted: 'Sessions started',
  minutesLearned: 'Minutes learned',
};

const initialValues = {
  title: '',
  targetType: '',
  targetValue: '',
  order: '',
  isActive: true,
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: DailyMissionTemplate;
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const missionInitialValues = {
    title: modalState?.title,
    targetType: modalState?.targetType,
    targetValue: modalState?.targetValue,
    order: modalState?.order,
    isActive: modalState?.isActive ?? true,
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
      targetValue: Number(values.targetValue),
      order: Number(values.order),
    });
  };

  return (
    <Formik
      initialValues={modalState ? missionInitialValues : initialValues}
      validationSchema={MinimalDailyMissionTemplateSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched, setFieldValue }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Title</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.title}
                    onChangeText={handleChange}
                    name={'title'}
                    errorMessage={errors.title && touched.title ? errors?.title : undefined}
                    placeholder={'e.g. Start 2 sessions'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Target type</Components.FieldLabel>
                  <ProjectSelect
                    value={values.targetType}
                    processShownLabel={(value) => TARGET_TYPE_LABELS[value as MissionTargetType]}
                    onChangeSelectedItem={handleChange}
                    name={'targetType'}
                    errorMessage={errors.targetType && touched.targetType ? (errors?.targetType as string) : undefined}
                    placeholder={'Select target type'}>
                    {MISSION_TARGET_TYPES.map((key) => (
                      <Components.MenuItem key={key} value={key}>
                        {TARGET_TYPE_LABELS[key]}
                      </Components.MenuItem>
                    ))}
                  </ProjectSelect>
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>
                    Target value {values.targetType === 'minutesLearned' ? '(minutes)' : '(sessions)'}
                  </Components.FieldLabel>
                  <ProjectTextField
                    value={values?.targetValue?.toString() ?? ''}
                    onChangeText={handleChange}
                    name={'targetValue'}
                    errorMessage={errors.targetValue && touched.targetValue ? errors?.targetValue : undefined}
                    placeholder={'e.g. 2'}
                    {...textFieldUtils.inputPositiveNumber}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Order</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.order?.toString() ?? ''}
                    onChangeText={handleChange}
                    name={'order'}
                    errorMessage={errors.order && touched.order ? errors?.order : undefined}
                    placeholder={'Display order, e.g. 1'}
                    {...textFieldUtils.inputPositiveNumber}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox
                    checked={values?.isActive}
                    onChange={(e) => setFieldValue('isActive', e.target.checked)}
                    name={'isActive'}
                  />
                  <Components.CheckboxLabel>Active</Components.CheckboxLabel>
                </Components.CheckboxFieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditDailyMissionTemplateModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { mission?: DailyMissionTemplate };
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreateDailyMissionTemplateDto = {
      title: values?.title,
      targetType: values?.targetType,
      targetValue: values?.targetValue,
      order: values?.order,
      isActive: values?.isActive,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.mission?.id
        ? await updateDailyMissionTemplate({ ...data, id: modalState?.mission?.id })
        : await createDailyMissionTemplate(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.mission?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a daily mission');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.mission} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.mission ? `Edit daily mission` : 'Create new daily mission'}
        titleSuffix={modalState?.mission ? `(ID: ${modalState?.mission?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditDailyMissionTemplateModal;
